import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { communityCopyV13, communityCardsV13, communityShareCardV13 } from "../config/media";

const EASE = [0.16, 1, 0.3, 1];
const CARD_W = 300; // px — portrait 4:5 → 375px tall

const CardMedia = ({ media }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.4 });
  useEffect(() => {
    const v = ref.current;
    if (!v || media.type !== "video") return;
    if (inView) v.play().catch(() => {});
    else v.pause();
  }, [inView, media.type]);

  const cls = "absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]";
  if (media.type === "video") {
    return (
      <video ref={ref} data-testid="community-card-video" poster={media.poster} aria-label={media.alt} muted loop playsInline preload="metadata" className={cls} style={{ objectPosition: media.position }}>
        <source src={media.mp4} type="video/mp4" />
        <source src={media.webm} type="video/webm" />
      </video>
    );
  }
  return <img ref={ref} src={media.url} alt={media.alt} className={cls} style={{ objectPosition: media.position }} draggable={false} />;
};

// V1.3 Refinement 13 — Community cards rail (V1.3 ONLY).
// Portrait cards with identity + caption + mode tag; snap/drag/arrows; "Share yours" end card.
export const CommunitySectionV13 = () => {
  const railRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const onScroll = () => {
    const el = railRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const p = max > 0 ? el.scrollLeft / max : 0;
    setProgress(p);
    setAtStart(el.scrollLeft < 8);
    setAtEnd(el.scrollLeft > max - 8);
  };

  const scrollBy = (dir) => {
    railRef.current?.scrollBy({ left: dir * (CARD_W + 20) * 2, behavior: "smooth" });
  };

  // Desktop swipe: click-and-drag to scroll the rail (snap disabled while dragging).
  const drag = useRef({ active: false, startX: 0, startLeft: 0, moved: false });
  const [dragging, setDragging] = useState(false);

  const onPointerDown = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const el = railRef.current;
    drag.current = { active: true, startX: e.clientX, startLeft: el.scrollLeft, moved: false };
    setDragging(true);
  };
  const onPointerMove = (e) => {
    if (!drag.current.active) return;
    const dx = e.clientX - drag.current.startX;
    if (Math.abs(dx) > 4) drag.current.moved = true;
    railRef.current.scrollLeft = drag.current.startLeft - dx;
  };
  const endDrag = () => {
    if (!drag.current.active) return;
    drag.current.active = false;
    setDragging(false);
  };
  const onClickCapture = (e) => {
    if (drag.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      drag.current.moved = false;
    }
  };

  return (
    <section id="community" data-testid="community-section" className="bg-[var(--strollee-surface)] py-20 lg:py-24">
      <div className="px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="flex items-end justify-between gap-12"
        >
          <div className="max-w-[640px]">
            <p data-testid="community-eyebrow" className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--strollee-accent)]">
              {communityCopyV13.eyebrow}
            </p>
            <h2 className="mt-4 text-[36px] font-semibold leading-[1.1] tracking-[-0.03em] text-[var(--strollee-ink)] lg:text-[48px]">
              {communityCopyV13.headlineLines.map((line) => (
                <span key={line} className="block">{line}</span>
              ))}
            </h2>
          </div>
          <div className="flex flex-col items-end gap-6">
            <p className="max-w-[340px] text-right text-base leading-relaxed text-[var(--strollee-muted)]">
              {communityCopyV13.support}
            </p>
            <div className="flex items-center gap-4">
              <div className="h-px w-24 bg-[var(--strollee-line)]">
                <div data-testid="community-rail-progress" className="h-px bg-[var(--strollee-ink)] transition-[width] duration-200" style={{ width: `${Math.max(8, progress * 100)}%` }} />
              </div>
              <button type="button" aria-label="Previous" data-testid="community-prev-btn" onClick={() => scrollBy(-1)} disabled={atStart}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--strollee-line)] text-[var(--strollee-ink)] transition-all duration-200 hover:border-[var(--strollee-ink)] disabled:opacity-30 disabled:hover:border-[var(--strollee-line)]">
                <ArrowLeft size={16} strokeWidth={2} />
              </button>
              <button type="button" aria-label="Next" data-testid="community-next-btn" onClick={() => scrollBy(1)} disabled={atEnd}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--strollee-line)] text-[var(--strollee-ink)] transition-all duration-200 hover:border-[var(--strollee-ink)] disabled:opacity-30 disabled:hover:border-[var(--strollee-line)]">
                <ArrowRight size={16} strokeWidth={2} />
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      {/* RAIL — bleeds off the right edge */}
      <div
        ref={railRef}
        onScroll={onScroll}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onPointerCancel={endDrag}
        onClickCapture={onClickCapture}
        data-testid="community-rail"
        data-dragging={dragging}
        className={`mt-10 flex gap-5 overflow-x-auto px-6 pb-2 scroll-pl-6 lg:mt-12 lg:px-10 lg:scroll-pl-10 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden select-none ${
          dragging ? "cursor-grabbing snap-none" : "cursor-grab snap-x snap-mandatory"
        }`}
      >
        {communityCardsV13.map((card, i) => (
          <motion.article
            key={card.id}
            data-testid={`community-card-${card.id}`}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, ease: EASE, delay: Math.min(i, 4) * 0.07 }}
            className="group relative aspect-[4/5] shrink-0 snap-start overflow-hidden rounded-[var(--strollee-radius-module)] bg-[var(--strollee-dark)]"
            style={{ width: CARD_W }}
          >
            <CardMedia media={card.media} />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0F1A15]/85 via-[#0F1A15]/20 via-50% to-transparent" />
            <span data-testid={`community-mode-${card.id}`} className="absolute left-4 top-4 rounded-full bg-[#0F1A15]/80 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white backdrop-blur-md">
              {card.mode}
            </span>
            <div className="absolute inset-x-0 bottom-0 p-5 text-white">
              <div className="flex items-center gap-2.5">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/15 text-[11px] font-semibold backdrop-blur-md">
                  {card.name[0]}
                </span>
                <p className="text-[13px] font-semibold">
                  {card.name} <span className="font-medium text-white/55">· {card.city}</span>
                </p>
              </div>
              <p data-testid={`community-caption-${card.id}`} className="mt-3 text-[15px] font-medium leading-[1.35] tracking-[-0.01em]">
                {card.caption}
              </p>
            </div>
          </motion.article>
        ))}

        {/* END CARD — participation */}
        <motion.a
          href="#stay-in-the-loop"
          data-testid="community-share-card"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.7, ease: EASE }}
          className="group relative flex aspect-[4/5] shrink-0 snap-start flex-col justify-between overflow-hidden rounded-[var(--strollee-radius-module)] bg-[var(--strollee-dark)] p-6 text-[var(--strollee-bg)]"
          style={{ width: CARD_W }}
        >
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#8FB89E]/20 blur-[60px]" />
          <p className="relative text-[11px] font-semibold uppercase tracking-[0.2em] text-[#8FB89E]">{communityShareCardV13.eyebrow}</p>
          <div className="relative">
            <p className="text-[34px] font-semibold leading-[1.02] tracking-[-0.03em]">{communityShareCardV13.title}</p>
            <p className="mt-1 text-[22px] font-semibold tracking-[-0.02em] text-[#8FB89E]">{communityShareCardV13.tag}</p>
            <p className="mt-4 text-[14px] leading-relaxed text-[var(--strollee-bg)]/65">{communityShareCardV13.copy}</p>
            <span className="mt-6 inline-flex items-center gap-2 text-[13px] font-semibold transition-colors duration-200 group-hover:text-[#8FB89E]">
              {communityShareCardV13.cta}
              <ArrowUpRight size={15} strokeWidth={2.2} className="transition-transform duration-200 group-hover:translate-x-[2px] group-hover:-translate-y-[2px]" />
            </span>
          </div>
        </motion.a>
        <div className="w-4 shrink-0 lg:w-8" aria-hidden="true" />
      </div>

      {/* FOOTER — stat + human quote */}
      <div className="px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
          className="mt-12 grid grid-cols-12 items-center gap-10 border-t border-[var(--strollee-line)] pt-10 lg:mt-14"
        >
          <div data-testid="community-stat" className="col-span-3">
            <p className="text-[52px] font-semibold leading-none tracking-[-0.03em] text-[var(--strollee-ink)] lg:text-[64px]">{communityCopyV13.statNumber}</p>
            <p className="mt-3 text-[12px] font-semibold uppercase tracking-[0.16em] text-[var(--strollee-muted)]">{communityCopyV13.statLabel}</p>
          </div>
          <blockquote data-testid="community-quote" className="col-span-9 border-l border-[var(--strollee-line)] pl-10">
            <p className="text-[19px] font-medium leading-[1.4] tracking-[-0.01em] text-[var(--strollee-ink)] lg:text-[22px]">“{communityCopyV13.quote}”</p>
            <footer className="mt-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--strollee-muted)]">{communityCopyV13.quoteAttribution}</footer>
          </blockquote>
        </motion.div>
      </div>
    </section>
  );
};
